import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../auth/api";
import { entityRows } from "../lib/api";
import { t } from "../i18n";

// CEO Cockpit + CFO Corner — rich executive tiles (legacy kpiTile look: colored
// left border, traffic-light status, sub-line, source/target footer, badges) plus
// an auto-generated insights strip. Consumes the legacy-shaped dashboard responses.

const cad = (v: number) =>
  "$" + (v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Traffic light: green ≤ goodMax, amber ≤ warnMax, else red. (legacy trafficLight)
function light(val: number, goodMax: number, warnMax: number): string {
  const v = Math.abs(val || 0);
  if (v <= goodMax) return "#059669";
  if (v <= warnMax) return "#d97706";
  return "#dc2626";
}

interface Badge { text: string; bg: string; color: string }
interface TileProps {
  label: string; value: string; sub?: string; color?: string;
  badge?: Badge | null; context?: string; target?: string; source?: string; onClick?: () => void;
}
function KpiTile({ label, value, sub, color = "#0c0a08", badge, context, target, source, onClick }: TileProps) {
  const ctx = context ? t(context) : "";
  const src = source ? t(source) : "";
  const foot = ctx ? `${target ? `${ctx} · ${t("target")} ${t(target)}` : ctx}${src ? ` · ${src}` : ""}` : src;
  return (
    <article className={`kpi-tile${onClick ? " clickable" : ""}`} style={{ borderLeftColor: color }} onClick={onClick} title={foot || undefined}>
      <div className="kpi-tile__top">
        <span className="kpi-tile__label">{t(label)}</span>
        {badge && <span className="kpi-tile__badge" style={{ background: badge.bg, color: badge.color }}>{badge.text}</span>}
      </div>
      <div className="kpi-tile__value" style={{ color }}>{value}</div>
      {sub && <div className="kpi-tile__sub">{t(sub)}</div>}
      {foot && <div className="kpi-tile__foot">{foot}</div>}
      {onClick && <div className="kpi-tile__hint">↗ {t("Click for detail")}</div>}
    </article>
  );
}

function Insights({ items }: { items: string[] }) {
  return (
    <section className="insights">
      <b>📊 {t("Period insights (auto-generated)")}</b>
      {items.length ? (
        <ul>{items.slice(0, 5).map((s, i) => <li key={i}>{s}</li>)}</ul>
      ) : <ul><li>{t("All key indicators within target.")}</li></ul>}
    </section>
  );
}

function Exec({ titleKey, subtitle, children }: { titleKey: string; subtitle: string; children: ReactNode }) {
  return (
    <div className="page">
      <div className="page-head"><h2>{t(titleKey)}</h2></div>
      <p className="exec-sub">{t(subtitle)}</p>
      {children}
    </div>
  );
}

interface CustBal { cust_id: string; balance: number }
interface Geo { setid: string; revenue: number }
interface Ceo {
  revenue_mtd: number; revenue_ytd: number; bills_mtd_count: number;
  operating_margin_pct: number; revenue_ytd_gl: number; expense_ytd_gl: number;
  cash_position: number; pipeline_value: number; top_customers: CustBal[];
  concentration_top1_pct: number; geographic_top: Geo | null;
  risk_score: number; material_weakness_count: number;
  active_business_units: number; active_users: number;
}

type Drill = { kind: "customer"; id: string } | { kind: "risk" } | null;

export function CEOPage() {
  const nav = useNavigate();
  const [d, setD] = useState<Ceo | null>(null);
  const [error, setError] = useState("");
  const [drill, setDrill] = useState<Drill>(null);
  const [detail, setDetail] = useState<Record<string, unknown>[]>([]);
  useEffect(() => {
    apiFetch("/api/v1/dashboards/ceo").then((r) => r.json()).then(setD).catch(() => setError("failed to load"));
  }, []);
  useEffect(() => {
    if (!drill) { setDetail([]); return; }
    const tbl = drill.kind === "customer" ? "ps_ar_pending_item" : "ps_int_audit";
    entityRows<Record<string, unknown>>(tbl).then((rows) => {
      setDetail(drill.kind === "customer"
        ? rows.filter((r) => r.customer_id === drill.id && ["OPEN", "PARTIAL"].includes(String(r.item_status)))
        : rows.filter((r) => ["OPEN", "IN_PROGRESS"].includes(String(r.status))));
    }).catch(() => setDetail([]));
  }, [drill]);
  if (error) return <div className="page"><div className="error">{error}</div></div>;
  if (!d) return <div className="page"><h2>{t("nav.ceo")}</h2></div>;

  const mw = d.material_weakness_count > 0;
  const conc = d.concentration_top1_pct;
  const top1 = d.top_customers[0];
  const insights: string[] = [];
  if (d.revenue_ytd_gl > 0 && d.operating_margin_pct < 15) insights.push(t("Operating margin {pct}% below the sector benchmark (>20%). Review operating expense.", { pct: d.operating_margin_pct }));
  if (conc > 30) insights.push(t("Top-1 customer concentration {pct}% > 30% — single-customer dependence risk.", { pct: conc }));
  if (mw) insights.push(t("{n} Material Weakness identified — 10-K SOX 404 disclosure required.", { n: d.material_weakness_count }));
  if (d.cash_position < 50000) insights.push(t("Cash position {amt} below the $50K threshold — activate a 13-week forecast.", { amt: cad(d.cash_position) }));
  if (d.pipeline_value > d.revenue_ytd) insights.push(t("Pipeline {pl} > Revenue YTD {rev} — strong leading indicator.", { pl: cad(d.pipeline_value), rev: cad(d.revenue_ytd) }));

  return (
    <Exec titleKey="nav.ceo" subtitle="Strategic view · 8 KPIs · click a tile to drill down.">
      <div className="exec-grid">
        <KpiTile label={t("kpi.revenue_mtd")} value={cad(d.revenue_mtd)} sub={t("YTD {ytd} · {n} invoices", { ytd: cad(d.revenue_ytd), n: d.bills_mtd_count })} color="#A16207" context="Revenue billed this month" source="BI" onClick={() => nav("/bi")} />
        <KpiTile label={t("kpi.operating_margin_pct")} value={`${d.operating_margin_pct}%`} sub={t("Rev {rev} − Exp {exp}", { rev: cad(d.revenue_ytd_gl), exp: cad(d.expense_ytd_gl) })} color={light(40 - d.operating_margin_pct, 20, 35)} context="(Revenue − OpEx) / Revenue, YTD" target=">20%" source="GL" onClick={() => nav("/gl?t=reports")} />
        <KpiTile label={t("kpi.cash_position")} value={cad(d.cash_position)} sub="Cash + operating banks (1010/1020)" color={light(d.cash_position > 100000 ? 0 : 100, 50, 90)} context="Net cash + banks" target=">$100k" source="GL" onClick={() => nav("/gl?t=balances")} />
        <KpiTile label={t("kpi.pipeline_value")} value={cad(d.pipeline_value)} sub="Open purchase-order commitments" color="#0284C7" context="Leading indicator of future spend" source="PO" onClick={() => nav("/po")} />
        <KpiTile label={t("kpi.top_customers")} value={String(d.top_customers.length)} sub={top1 ? t("#1 {id} · {pct}% of total AR", { id: top1.cust_id, pct: conc }) : "No AR balances"} color={conc > 30 ? "#d97706" : "#059669"} badge={conc > 30 ? { text: "RISK", bg: "#fef3c7", color: "#92400e" } : null} context="Top 5 by open balance" target="<30% concentration" source="AR" onClick={() => setDrill(drill?.kind === "customer" ? null : (top1 ? { kind: "customer", id: top1.cust_id } : null))} />
        <KpiTile label={t("kpi.geographic_top")} value={d.geographic_top ? d.geographic_top.setid : "—"} sub={d.geographic_top ? t("{rev} leading region", { rev: cad(d.geographic_top.revenue) }) : "No data"} color="#0D9488" context="Leading SetID by revenue (geographic mix)" source="BI" />
        <KpiTile label={t("kpi.risk_score")} value={`${d.risk_score}${mw ? "*" : ""}`} sub={mw ? t("{n} Material Weakness — SOX 404", { n: d.material_weakness_count }) : t("{n} open findings", { n: d.risk_score })} color={mw ? "#dc2626" : d.risk_score > 5 ? "#d97706" : "#059669"} badge={mw ? { text: "MW", bg: "#fee2e2", color: "#991b1b" } : null} context="Open internal-audit findings" target="<5 · 0 MW" source="Audit" onClick={() => setDrill(drill?.kind === "risk" ? null : { kind: "risk" })} />
        <KpiTile label="Strategic" value={`${d.active_business_units} BUs · ${d.active_users}u`} sub="Operating footprint + active users" color="#7C3AED" context="Active business units and system users" source="GL" />
      </div>

      <Insights items={insights} />

      {d.top_customers.length > 0 && (
        <div className="card">
          <h3 className="card-title">{t("kpi.top_customers")}</h3>
          <table className="data"><tbody>
            {d.top_customers.map((c) => (
              <tr key={c.cust_id} className="drillable" onClick={() => setDrill(drill?.kind === "customer" && drill.id === c.cust_id ? null : { kind: "customer", id: c.cust_id })}>
                <td>{c.cust_id} ▸</td><td className="num">{cad(c.balance)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}
      {drill && (
        <div className="card">
          <h3 className="card-title">{drill.kind === "customer" ? t("dash.openItemsFor", { id: drill.id }) : t("dash.openFindings")}</h3>
          {detail.length ? (
            <table className="data">
              <thead><tr>{Object.keys(detail[0]).filter((k) => k !== "tenant_id").slice(0, 5).map((k) => <th key={k}>{k}</th>)}</tr></thead>
              <tbody>{detail.map((r, i) => <tr key={i}>{Object.keys(detail[0]).filter((k) => k !== "tenant_id").slice(0, 5).map((k) => <td key={k}>{String(r[k] ?? "")}</td>)}</tr>)}</tbody>
            </table>
          ) : <p className="empty">{t("common.noData")}</p>}
        </div>
      )}
    </Exec>
  );
}

interface AgingSeg { label: string; amount: number }
interface Cfo {
  dso_days: number; dpo_days: number; dio_days: number; ccc_days: number;
  ar_balance: number; ap_balance: number; revenue_ytd: number;
  close_pct: number; periods_closed: number; working_capital: number; current_ratio: number;
  sox_pct: number; findings_closed: number; findings_total: number; material_weakness_count: number;
  ar_aging_overdue: number; ap_aging_overdue: number; cash_13w: number; ar_aging: AgingSeg[];
}

const AGING_COLORS = ["#059669", "#0D9488", "#d97706", "#ea580c", "#dc2626"];

function AgingBar({ buckets }: { buckets: AgingSeg[] }) {
  const total = buckets.reduce((s, b) => s + b.amount, 0);
  return (
    <div className="card">
      <h3 className="card-title">{t("AR Aging Distribution")}</h3>
      {total > 0 ? (
        <>
          <div style={{ display: "flex", height: 30, borderRadius: 6, overflow: "hidden", border: "1px solid var(--warm-mist)" }}>
            {buckets.map((b, i) => b.amount > 0 && (
              <div key={i} style={{ width: `${(b.amount / total) * 100}%`, background: AGING_COLORS[i] }} title={`${b.label}: ${cad(b.amount)}`} />
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12, fontSize: 12.5 }}>
            {buckets.map((b, i) => (
              <span key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 11, height: 11, borderRadius: 2, background: AGING_COLORS[i] }} />
                {t(b.label)} · {cad(b.amount)}
              </span>
            ))}
          </div>
        </>
      ) : <p className="empty">{t("common.noData")}</p>}
    </div>
  );
}

// Close accounting actions (the legacy CFO "Acciones contables del cierre"
// playbook). Implemented steps deep-link to their Go screen; the rest to /soon.
const CLOSE_ACTIONS = [
  { n: 40, title: "Period-end AP accrual", desc: "Identify receipts without a voucher; create an auto-reversing accrual journal.", to: "/soon" },
  { n: 46, title: "Revenue accrual (BI/AR)", desc: "Bills invoiced for future service → deferred-revenue journal (2210).", to: "/soon" },
  { n: 49, title: "Subledger vs GL reconciliation", desc: "Compare AR/AP/Bank subledger to GL; report variances > $0.01.", to: "/soon" },
  { n: 42, title: "Bank reconciliation (BAI2)", desc: "Import a BAI2 statement; match payments; update RECONCILED_FLAG.", to: "/soon" },
  { n: 48, title: "Year-end close", desc: "Close P&L; zero-out I/E accounts to Retained Earnings (3110).", to: "/gl?t=yearend" },
  { n: 51, title: "PR regulatory reporting", desc: "SC 2918 (monthly IVU), SC 2070 (480.6 withholding), SURI returns.", to: "/compliance" },
  { n: 45, title: "SoD exceptions", desc: "Users with segregation-of-duties conflicts in the period, for internal audit.", to: "/soon" },
];

function CloseActions() {
  return (
    <>
      <h3 className="exec-section-title">{t("Close accounting actions")}</h3>
      <div className="action-grid">
        {CLOSE_ACTIONS.map((a) => (
          <Link key={a.n} to={a.to} className="action-card">
            <span className="at">{t(a.title)} <span className="an">#{a.n}</span></span>
            <span className="ad">{t(a.desc)}</span>
          </Link>
        ))}
      </div>
    </>
  );
}

export function CFOPage() {
  const nav = useNavigate();
  const [d, setD] = useState<Cfo | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    apiFetch("/api/v1/dashboards/cfo").then((r) => r.json()).then(setD).catch(() => setError("failed to load"));
  }, []);
  if (error) return <div className="page"><div className="error">{error}</div></div>;
  if (!d) return <div className="page"><h2>{t("nav.cfo")}</h2></div>;

  const mw = d.material_weakness_count > 0;
  const insights: string[] = [];
  if (d.dso_days > 45) insights.push(t("DSO {n}d above the 45-day target — accelerate collections.", { n: d.dso_days }));
  if (d.ccc_days > 30) insights.push(t("Cash conversion cycle {n}d above the 30-day target — working capital tied up in operations.", { n: d.ccc_days }));
  if (d.ccc_days < 0) insights.push(t("Negative cash conversion cycle ({n}d) — operations financed by suppliers. Strong position.", { n: d.ccc_days }));
  if (d.current_ratio > 0 && d.current_ratio < 1) insights.push(t("Current ratio {n} below 1.0 — current liabilities exceed current assets; liquidity watch.", { n: d.current_ratio }));
  if (mw) insights.push(t("{n} Material Weakness open — 10-K SOX 404 disclosure required.", { n: d.material_weakness_count }));
  if (d.ar_aging_overdue > 0) insights.push(t("{amt} of receivables overdue >60 days — CECL provision rises sharply on these buckets.", { amt: cad(d.ar_aging_overdue) }));

  const gray = "#94a3b8";
  return (
    <Exec titleKey="nav.cfo" subtitle="Financial view · 12 KPIs · working capital + close + SOX + tax.">
      <div className="exec-grid">
        <KpiTile label="Close Calendar" value={`${d.close_pct}%`} sub={t("{n} / 12 periods closed", { n: d.periods_closed })} color={light(100 - d.close_pct, 20, 50)} context="Fiscal-year close progress" target=">90% at close" source="GL · PeriodStatus" onClick={() => nav("/gl")} />
        <KpiTile label={t("kpi.dso_days")} value={`${d.dso_days}d`} sub={t("AR {ar} / Rev YTD", { ar: cad(d.ar_balance) })} color={light(d.dso_days, 45, 60)} context="(AR / Revenue YTD) × 365" target="<45 days" source="AR + BI" onClick={() => nav("/ar")} />
        <KpiTile label={t("kpi.dpo_days")} value={`${d.dpo_days}d`} sub={t("AP {ap} / COGS est.", { ap: cad(d.ap_balance) })} color={light(60 - d.dpo_days, 20, 40)} context="Days payable outstanding" target="30–60 days" source="AP" onClick={() => nav("/ap")} />
        <KpiTile label={t("kpi.ccc_days")} value={`${d.ccc_days}d`} sub={t("DSO {dso} + DIO {dio} − DPO {dpo}", { dso: d.dso_days, dio: d.dio_days, dpo: d.dpo_days })} color={light(d.ccc_days, 30, 60)} context="Cash conversion cycle" target="<30 days" source="Working capital" />
        <KpiTile label="Working Capital" value={cad(d.working_capital)} sub="Current assets − current liabilities" color={d.working_capital >= 0 ? "#059669" : "#dc2626"} context="Short-term liquidity buffer" source="GL · A/L balances" onClick={() => nav("/gl?t=balances")} />
        <KpiTile label="Current Ratio" value={d.current_ratio.toFixed(2)} sub="Current assets / current liabilities" color={light(2 - d.current_ratio, 0.5, 1)} context="Liquidity coverage" target=">1.5" source="GL · A/L balances" />
        <KpiTile label="P&L vs Budget" value="—" sub="No budget ledger loaded" color={gray} context="Actual vs BUDGET ledger" source="GL · BUDGET" />
        <KpiTile label="13-Week Cash" value={cad(d.cash_13w)} sub="Net inflows − outflows, 13 weeks" color={d.cash_13w >= 0 ? "#0284C7" : "#dc2626"} context="Short-term treasury forecast" source="Treasury · ps_treas_fcst" />
        <KpiTile label="Tax Provision" value="—" sub="Effective tax rate — not loaded" color={gray} context="ETR from tax provision" source="Tax" />
        <KpiTile label="SOX Status" value={`${d.sox_pct}%`} sub={t("{closed} / {total} findings closed", { closed: d.findings_closed, total: d.findings_total })} color={mw ? "#dc2626" : light(100 - d.sox_pct, 20, 50)} badge={mw ? { text: "MW", bg: "#fee2e2", color: "#991b1b" } : null} context="Internal-audit remediation" target=">90% · 0 MW" source="Audit" />
        <KpiTile label="AR Aging" value={cad(d.ar_aging_overdue)} sub="Receivables overdue > 60 days" color={d.ar_aging_overdue > 0 ? "#d97706" : "#059669"} context="CECL provision rises on +60d" source="AR" onClick={() => nav("/ar")} />
        <KpiTile label="AP Aging" value={cad(d.ap_aging_overdue)} sub="Payables overdue > 60 days" color={d.ap_aging_overdue > 0 ? "#d97706" : "#059669"} context="Vendor & legal risk on +60d" source="AP" onClick={() => nav("/ap")} />
        <KpiTile label="Material Weakness" value={String(d.material_weakness_count)} sub={mw ? "SOX 404 disclosure" : "None identified"} color={mw ? "#dc2626" : "#059669"} badge={mw ? { text: "MW", bg: "#fee2e2", color: "#991b1b" } : null} context="SOX §404 material weaknesses" source="Audit" />
      </div>
      <AgingBar buckets={d.ar_aging || []} />
      <Insights items={insights} />
      <CloseActions />
    </Exec>
  );
}
